import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { fetchTemplate, createTemplateApi, listCampaignsApi, getAllTemplates } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';
import ScoringStrategySelector from '../components/ScoringStrategySelector';
import ScoringModeSelector from '../components/ScoringModeSelector';
import { validateTemplateUI } from './ScoringConfigUI';
import { FaTimes } from 'react-icons/fa';

const emptyQuestion = { 
  questionName: '', 
  description: '', 
  type: 'Compliance', 
  weight: '', 
  expected: '', 
  autofail: false,
  // NEW: Modular scoring fields
  scoringStrategy: 'Binary',
  scoringConfig: {},
  questionWeight: 1,
  includeInScore: false
};

const TemplateEditor = ({ setAlert }) => {
  const history = useHistory();
  const { name } = useParams();
  const [templates, setTemplates] = useState()
  const [templateName, setTemplateName] = useState('');
  const [campaign, setCampaign] = useState('');
  const [owningCampaign, setOwningCampaign] = useState('');
  const [questions, setQuestions] = useState([ { ...emptyQuestion } ]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [inputError, setInputError] = useState([false]);
  const [saveCurrent, setSaveCurrent] = useState(false)
  const [sections, setSections] = useState([
    {
    sectionName: "",
    sectionWeight: "",
    scoringMethod: "Average",  // NEW: Section scoring method
    questions: []
    }
  ]);
  const [errors, setErrors] = useState({});
  // NEW: Template metadata state
  const [templateMetadata, setTemplateMetadata] = useState({
    scoringMode: 'Weighted',
    complianceThreshold: 0.8
  });


  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const res = await listCampaignsApi();
        setCampaigns(res.campaigns || []);
        const res2 = await getAllTemplates();
        setTemplates(res2.templates);
        setCampaignsLoaded(true);
      } catch (err) {
        console.error('Failed to load campaigns', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaigns.' });
      }
    };
    loadCampaigns();
  }, []);


  useEffect(() => {
    if (!name) {
      setLoading(false);
      setDirty(false);
      return;
    }
    const load = async () => {
      try {
        const res = await fetchTemplate(name);
        console.log("temps: ", res)
        setTemplateName(res.templateName);
        setOwningCampaign(res.campaignName || '');
        setCampaign(res.campaignName || '');
        
        // Auto-migrate legacy questions to new format
        const migratedQuestions = (res.questions || []).map(q => ({
          ...q,
          // Ensure new fields exist with defaults
          scoringStrategy: q.scoringStrategy || (q.type === 'Compliance' ? 'Binary' : 'Scale5'),
          scoringConfig: q.scoringConfig || {},
          questionWeight: q.questionWeight || 1,
          includeInScore: q.includeInScore !== undefined ? q.includeInScore : (q.type === 'Quality')
        }));
        
        setQuestions(migratedQuestions);
        
        // Auto-migrate legacy sections to new format
        const migratedSections = (res.sections || []).map(s => ({
          ...s,
          scoringMethod: s.scoringMethod || 'Average'
        }));
        
        setSections(migratedSections);
        
        // Load template metadata (with defaults for legacy templates)
        setTemplateMetadata({
          scoringMode: res.scoringMode || 'Weighted',
          complianceThreshold: res.complianceThreshold || 0.8
        });
        
      } catch (err) {
        console.error('Failed to fetch scorecard', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load scorecard.' });
      } finally {
        setLoading(false);
        setDirty(false);
      }
    };
    load();
  }, [name]);


  // useEffect(() => {
  //   const loadQs = async () => {
  //     setSection({
  //       ...section,
  //       questions: questions.map((q) => {
  //         if (q.type === "Compliance") {
  //           return { [q.questionName]: "Rating_1" };
  //         } else if (q.type === "Quality") {
  //           return { [q.questionName]: "Rating_5" };
  //         } else {
  //           return { [q.questionName]: "Unknown" };
  //         }
  //       })
  //     })
  //   };
  //   loadQs();
  // }, [questions]);

  const addQuestion = () => { setQuestions(q => [...q, { ...emptyQuestion }]); setDirty(true); };
  const updateQuestion = (index, key, value) => { setQuestions(q => q.map((item,i)=> i===index ? { ...item, [key]: value } : item)); setDirty(true); };
  const removeQuestion = (index) => { setQuestions(q => q.filter((_,i)=>i!==index)); setDirty(true); };

  const handleSave = async () => {
    if (dirty && name && templateName.trim() === name.trim()) {
      setInputError([true, `Please provide a different version name before saving.`])
      setSaveConfirm(false);
      return;
    }
    for (let i =0; i<= templates.length -1 ; i++) {
      if (templates[i].templateName === templateName) {
        setInputError([true, `Template name '${templates[i].templateName}' already exists.`])
        setSaveConfirm(false);
        return;
      }      
    }

    
    const validation = validateTemplateUI(
      { questions, sections }, 
      templateMetadata.scoringMode
    );
    
    if (!validation.valid) {
      setInputError([true, validation.message]);
      setSaveConfirm(false);
      return;
    }

    setSaving(true);
    try {
      const payload = name
        ? {
            isVersionCreation: true,
            newTemplateName: templateName,
            originalTemplateName: name,
            campaign: campaign || undefined,
            questions,
            sections: sections,
            // NEW: Template metadata
            scoringMode: templateMetadata.scoringMode,
            complianceThreshold: templateMetadata.complianceThreshold
          }
        : { 
            templateName, 
            campaign: campaign || undefined, 
            questions, 
            sections: sections,
            // NEW: Template metadata
            scoringMode: templateMetadata.scoringMode,
            complianceThreshold: templateMetadata.complianceThreshold
          };
        console.log("PAYLOAD: ", payload)
      await createTemplateApi(payload);
      setAlert?.({ variant: 'success', heading: 'Scorecard Saved', text: 'Scorecard saved successfully.' });
      setDirty(false);
      history.push('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCurrent = async () => {
    if (name != templateName) {
      setInputError([true, `Your scorecard name does not match the question set you are editing.
        Please enter the correct scorecard name.`])
      return;
    }

    // Validate template configuration
    const validation = validateTemplateUI(
      { questions, sections }, 
      templateMetadata.scoringMode
    );
    
    if (!validation.valid) {
      setInputError([true, validation.message]);
      setSaveCurrent(false);
      return;
    }

    setSaving(true);
    try {
      const payload = {
            currentTemplateName: templateName,
            campaign: campaign || undefined,
            questions,
            sections: sections,
            // NEW: Template metadata
            scoringMode: templateMetadata.scoringMode,
            complianceThreshold: templateMetadata.complianceThreshold
          };
        console.log("PAYLOAD Current: ", payload)
      await createTemplateApi(payload);
      setAlert?.({ variant: 'success', heading: 'Scorecard Updated', text: 'Scorecard updated successfully.' });
      setDirty(false);
      history.push('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
    }
  };

  const isDuplicateSectionName = (name, currentIdx) => {
    return sections.some(
      (s, idx) => idx !== currentIdx && s.sectionName.trim() === name.trim()
    );
  };


  const isQuestionUsed = (questionName, currentSectionIdx) => {
    return sections.some((section, idx) =>
      idx !== currentSectionIdx &&
      section.questions.some(obj => Object.keys(obj)[0] === questionName)
    );
  };

  if (loading) {
    return <div className="text-center mt-20 text-gray-500">Loading...</div>;
  }

  console.log("QUestions: ", questions)


  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6">

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{name ? 'Edit Scorecard' : 'New Scorecard'}</h1>
      </div>
      
      {/* Error Banner at the top */}
      {inputError[0] && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-base font-bold text-red-800">⚠️ Validation Error</h3>
              <div className="mt-2 text-sm text-red-700 font-medium">
                {inputError[1]}
              </div>
            </div>
            <div className="ml-auto pl-3">
              <button 
                onClick={() => setInputError([false])}
                className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Scorecard Name</label>
          <input 
          className={`input-field ${inputError ? "input-error" : ""}`}
          autoFocus={inputError[0]}
          value={templateName} 
          onChange={e=>{setTemplateName(e.target.value); setDirty(true);}} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Owning Queue</label>
          <select
            value={campaign}
            onChange={e => { setCampaign(e.target.value); setDirty(true); }}
            className="select-field"
            disabled={!campaignsLoaded || (!!name && !!owningCampaign)}
          >
            {!campaignsLoaded
              ? <option value="">Loading queues...</option>
              : <>
                  <option value="">None</option>
                  {campaigns.map(c => (
                    <option key={c.campaignName} value={c.campaignName}>{c.campaignName}</option>
                  ))}
                </>
            }
          </select>
          {!!name && !!owningCampaign && (
            <p className="text-xs text-gray-500 mt-1">
              Owning queue is locked. To reassign, remove the scorecard from its current queue first.
            </p>
          )}
        </div>
        
        {/* NEW: Template-level scoring configuration */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Scoring Configuration</h3>
          <ScoringModeSelector
            value={templateMetadata.scoringMode}
            onChange={(mode) => {
              setTemplateMetadata({...templateMetadata, scoringMode: mode});

              if (mode === 'CustomWeightQuestions') {
                // Force all Quality questions to use CustomWeight strategy
                setQuestions(questions.map(q => {
                  if (q.type === 'Quality') {
                    return {
                      ...q,
                      scoringStrategy: 'CustomWeight',
                      scoringConfig: q.scoringConfig?.weightPct !== undefined
                        ? q.scoringConfig
                        : { minScore: 1, maxScore: 5, weightPct: 0 },
                      includeInScore: true
                    };
                  }
                  return q;
                }));
              } else {
                // Reset CustomWeight questions back to Scale5 when leaving this mode
                setQuestions(questions.map(q => {
                  if (q.type === 'Quality' && q.scoringStrategy === 'CustomWeight') {
                    return {
                      ...q,
                      scoringStrategy: 'Scale5',
                      scoringConfig: {}
                    };
                  }
                  return q;
                }));
              }

              setDirty(true);
            }}
            threshold={templateMetadata.complianceThreshold}
            onThresholdChange={(threshold) => {
              setTemplateMetadata({...templateMetadata, complianceThreshold: threshold});
              setDirty(true);
            }}
          />

          {/* Live weight total indicator for CustomWeightQuestions mode */}
          {templateMetadata.scoringMode === 'CustomWeightQuestions' && (() => {
            const qualityQuestions = questions.filter(q => q.type === 'Quality');
            const totalWeight = qualityQuestions.reduce(
              (sum, q) => sum + (parseFloat(q.scoringConfig?.weightPct) || 0), 0
            );
            const isValid = Math.abs(totalWeight - 100) <= 0.01;

            return (
              <div className={`mt-3 p-3 rounded-lg border ${
                isValid
                  ? 'bg-green-50 border-green-300 text-green-800'
                  : 'bg-yellow-50 border-yellow-300 text-yellow-800'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Quality Question Weight Total: {totalWeight.toFixed(1)}%
                  </span>
                  <span className="text-sm">
                    {isValid
                      ? 'Weights sum to 100%'
                      : `Weights must sum to 100% (currently ${totalWeight.toFixed(1)}%)`
                    }
                  </span>
                </div>
                {qualityQuestions.length === 0 && (
                  <p className="text-xs mt-1">No Quality questions defined yet.</p>
                )}
              </div>
            );
          })()}
        </div>

        <div className="space-y-6">
          {questions.map((q, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input value={q.questionName} onChange={e=>updateQuestion(idx,'questionName',e.target.value)} className="input-field" placeholder="Name" />
                <select value={q.type} onChange={e=>updateQuestion(idx,'type',e.target.value)} className="select-field">
                  <option value="Compliance">Compliance</option>
                  <option value="Quality">Quality</option>
                </select>
              </div>
              <textarea rows="2" value={q.description} onChange={e=>updateQuestion(idx,'description',e.target.value)} className="input-field w-full" placeholder="Description" />
              <textarea rows="2" value={q.expected || ''} onChange={e=>updateQuestion(idx,'expected',e.target.value)} className="input-field w-full" placeholder="Expected" />
              
              {/* Only show scoring strategy for Quality questions */}
              {q.type === 'Quality' && (
                <>
                  {/* NEW: Scoring Strategy Selector */}
                  <ScoringStrategySelector
                    value={q.scoringStrategy || 'Binary'}
                    onChange={(strategy) => updateQuestion(idx, 'scoringStrategy', strategy)}
                    config={q.scoringConfig || {}}
                    onConfigChange={(config) => updateQuestion(idx, 'scoringConfig', config)}
                    templateMode={templateMetadata.scoringMode}
                  />
                  
                </>
              )}
              
              {/* Only show auto-fail for Compliance questions */}
              {q.type === 'Compliance' && (
                <div>
                  <h4 style={{paddingLeft: '5px'}}><b>Auto-fail</b></h4>
                  <select value={q.autofail} onChange={e=>updateQuestion(idx,'autofail',e.target.value === 'true')} className="select-field" placeholder="Auto-fail" >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>                  
                </div>
              )}
              <button onClick={() => removeQuestion(idx)} className="btn-outline text-red-600 border-red-300 hover:bg-red-50">Remove</button>
            </div>
          ))}
          <button onClick={addQuestion} className="btn-outline">Add Question</button>
        </div>
        {inputError[0] && (
          <div className="custom-alert">
            <span>{inputError[1]}</span>
            <button className="dismiss-btn" onClick={() => setInputError([false])}>
              <FaTimes />
            </button>
          </div>
        )}
      </div>
      <div className='card space-y-4'>
      {sections.map((section, sIdx) => (
        <div key={sIdx} className="border border-gray-300 rounded p-4 mb-6 space-y-4">
          <h3 className="font-semibold text-lg text-gray-800">
            Section {sIdx + 1}
          </h3>

          {/* Section Name + Weight */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              className="input-field"
              placeholder="Section Name"
              value={section.sectionName}
              onChange={e => {
                const newSections = [...sections];
                const newName = e.target.value;

                if (isDuplicateSectionName(newName, sIdx)) {
                  setErrors(prev => ({
                    ...prev,
                    [`section-${sIdx}`]: `The name ${newName} is already in use.`
                  }));
                } else {
                  setErrors(prev => {
                    const copy = { ...prev };
                    delete copy[`section-${sIdx}`];
                    return copy;
                  });
                newSections[sIdx].sectionName = newName;
                setSections(newSections);
                }
              }}
            />
            {templateMetadata.scoringMode !== 'CustomWeightQuestions' && (
              <input
                type="text"
                className="input-field"
                placeholder="Overall Weight (e.g. 1-100)"
                value={section.sectionWeight}
                onChange={e => {
                  const newSections = [...sections];
                  newSections[sIdx].sectionWeight = e.target.value;
                  setSections(newSections);
                }}
              />
            )}
            {errors[`section-${sIdx}`] && (
              <p className="text-red-500 text-sm">{errors[`section-${sIdx}`]}</p>
            )}            
          </div>

          {/* Add Question to Section */}
          <div className="flex gap-2 items-center">
            <select
              className="select-field flex-1"
              onChange={e => {
                const selectedName = e.target.value;
                if (!selectedName) return;

                if (isQuestionUsed(selectedName, sIdx)) {
                  setErrors(prev => ({
                    ...prev,
                    [`question-${sIdx}`]: `The question "${selectedName}" is already assigned to another section`
                  }));
                  return;
                }

                setErrors(prev => {
                  const copy = { ...prev };
                  delete copy[`question-${sIdx}`];
                  return copy;
                });
                const q = questions.find(q => q.questionName === selectedName);
                if (!q) return;

                let newQuestion;
                if (templateMetadata.scoringMode === 'CustomWeightQuestions' && q.type === 'Quality') {
                  newQuestion = {
                    [q.questionName]: {
                      scoringStrategy: q.scoringStrategy || 'CustomWeight',
                      config: q.scoringConfig || { minScore: 1, maxScore: 5, weightPct: 0 },
                      includeInScore: true
                    }
                  };
                } else {
                  newQuestion = {
                    [q.questionName]: q.type === "Compliance" ? "Rating_1" : "Rating_5"
                  };
                }

                const newSections = [...sections];
                // prevent duplicates
                if (
                  !newSections[sIdx].questions.some(
                    obj => Object.keys(obj)[0] === q.questionName
                  )
                ) {
                  newSections[sIdx].questions.push(newQuestion);
                }
                setSections(newSections);
              }}
            >
              <option value=""> Add Question</option>
              {questions.map((q, idx) => (
                <option key={idx} value={q.questionName} disabled={isQuestionUsed(q.questionName, sIdx)}>

                  {q.questionName} : {q.type}
                </option>
              ))}
            </select>
            {errors[`question-${sIdx}`] && (
              <p className="text-red-500 text-sm">{errors[`question-${sIdx}`]}</p>
            )}
          </div>

          {/* List of Questions in this Section */}
          <div className="space-y-2">
            {section.questions.map((qObj, qIdx) => {
              const qName = Object.keys(qObj)[0];
              const qValue = qObj[qName];
              const matchedQuestion = questions.find(q => q.questionName === qName);
              const showWeight = templateMetadata.scoringMode === 'CustomWeightQuestions'
                && matchedQuestion?.type === 'Quality';
              const weightPct = showWeight
                ? (parseFloat(matchedQuestion?.scoringConfig?.weightPct) || 0)
                : null;

              return (
                <div
                  key={qIdx}
                  className="flex justify-between items-center border rounded p-2 bg-gray-50"
                >
                  <span>
                    <b>{qName}</b>
                    {showWeight && (
                      <span className="ml-2 text-sm text-gray-500 font-normal">
                        ({weightPct}%)
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="text-red-600 hover:underline text-sm"
                    onClick={() => {
                      const newSections = [...sections];
                      newSections[sIdx].questions = newSections[sIdx].questions.filter(
                        (obj, idx) => idx !== qIdx
                      );
                      setSections(newSections);
                    }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>

          {/* Remove Section */}
          <div className="flex justify-end">
            <button
              type="button"
              className="text-red-600 hover:underline text-sm"
              onClick={() => {
                setSections(sections.filter((_, idx) => idx !== sIdx));
              }}
            >
              Remove Section
            </button>
          </div>
        </div>
      ))}

      {/* Add New Section */}
      <button
        type="button"
        className="btn-outline"
        onClick={() =>
          setSections([
            ...sections,
            { sectionName: "", sectionWeight: "", questions: [] }
          ])
        }
      >
        Add Section
      </button>   
             
      </div>
      
      {/* Validation Error above Save Buttons */}
      {inputError[0] && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-base font-bold text-red-800">⚠️ Validation Error</h3>
              <div className="mt-2 text-sm text-red-700 font-medium">
                {inputError[1]}
              </div>
            </div>
            <div className="ml-auto pl-3">
              <button 
                onClick={() => setInputError([false])}
                className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end mb-6">
            <button onClick={() => setSaveConfirm(true)} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save New Version'}
            </button>
            {name && (
              <button style={{marginLeft: '15px'}} onClick={() => setSaveCurrent(true)} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Overwrite Current Version'}
              </button>      
            )}          
          </div>      

    </div>
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${templateName || 'this scorecard'}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    <ConfirmationModal
      open={saveCurrent}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to the current scorecard ${name || ''}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveCurrent(false)}
      onConfirm={handleSaveCurrent}
    />
    </>
  );
};

export default TemplateEditor;